import { memo, useLayoutEffect, useMemo, useRef } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Alert,
  Badge,
  Box,
  Checkbox,
  Flex,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import { TABLE_HEADER_CELL_STYLE } from '~/components/common/MasterDataTable'
import { Modal } from '~/components/common/Modal'
import { SaveCancelFooter } from '~/components/common/Modal/SaveCancelFooter'
import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import { formatDateDisplay } from '~/models/dateTime'
import { ApproverIdentityNotice } from '~/pages/SpotBidding/components/ApproverIdentityNotice'
import type { BulkApprovalFlow } from '~/pages/SpotBidding/hooks/useBulkApprovalFlow'
import { useBulkSelection } from '~/pages/SpotBidding/hooks/useBulkSelection'
import { useVirtualRows } from '~/pages/SpotBidding/hooks/useVirtualRows'
import type { BulkApprovalCase } from '~/pages/SpotBidding/models/bulkApproval'
import {
  countViolationDetails,
  hasBulkUnverified,
  hasBulkViolation,
} from '~/pages/SpotBidding/models/bulkApproval'

type Props = {
  flow: BulkApprovalFlow
  /** 受渡日 (= 表示中の受渡日の承認待ちだけが対象であることを示す)。 */
  targetDate: Date
}

const mw = (value: number): string => value.toFixed(1)

type RowProps = {
  c: BulkApprovalCase
  checked: boolean
  onToggle: (id: string, checked: boolean) => void
  /** 描いた行の高さを知らせる (見えている行だけを描く表の位置合わせ用)。 */
  onMeasure: (id: string, height: number) => void
}

const COLUMN_COUNT = 7

/** 描かない行の高さを保つ空行 (スクロールバーの長さと位置を全行描いた時と揃える)。 */
const SpacerRow = ({
  height,
}: {
  height: number
}): React.ReactElement | null =>
  height <= 0 ? null : (
    <TableRow aria-hidden>
      <TableCell
        colSpan={COLUMN_COUNT}
        style={{ height, padding: 0, border: 'none' }}
      />
    </TableRow>
  )

/** 案件 1 行。 数千行でも選択の切替で全行を描き直さないよう memo する。 */
const CaseRow = memo(function CaseRow({
  c,
  checked,
  onToggle,
  onMeasure,
}: RowProps): React.ReactElement {
  const { t } = useTranslation()
  const rowRef = useRef<HTMLTableRowElement>(null)
  useLayoutEffect(() => {
    if (rowRef.current !== null) {
      onMeasure(c.id, rowRef.current.getBoundingClientRect().height)
    }
  })
  const resolveDisplayName = useMemberDisplayName()
  const violationCount = countViolationDetails(c.violationSections)
  const inversionDetails = c.warningSections.flatMap((s) => s.details)
  return (
    <TableRow ref={rowRef}>
      <TableCell verticalAlign="top">
        <Checkbox
          checked={checked}
          inputProps={{ 'aria-label': c.title }}
          onCheckedChange={(details: {
            checked: boolean | 'indeterminate'
          }) => {
            onToggle(c.id, details.checked === true)
          }}
        />
      </TableCell>
      <TableCell verticalAlign="top">
        <Text fontWeight="semibold">{c.title}</Text>
        {/* 申請者と内容 (札数 / コマ範囲) は案件名の下に添える (design: 列にすると
            1536px でも列が入らず語の途中で折れる)。 */}
        <Text color="Text.midEmphasis" fontSize="xs">
          {resolveDisplayName(c.requestedBy) ?? '—'}
          {t('page:biddingCommon.bulkApprove.table.metaSeparator')}
          {c.detail}
        </Text>
        <ApproverIdentityNotice identity={c.identity} />
      </TableCell>
      <TableCell verticalAlign="top" whiteSpace="nowrap">
        {c.areaRows
          .map((row) => row.areaLabel)
          .join(t('page:biddingCommon.bulkApprove.table.areaSeparator'))}
      </TableCell>
      <TableCell verticalAlign="top" textAlign="right" whiteSpace="nowrap">
        {c.buyMw > 0 ? mw(c.buyMw) : '—'}
      </TableCell>
      <TableCell verticalAlign="top" textAlign="right" whiteSpace="nowrap">
        {c.sellMw > 0 ? mw(c.sellMw) : '—'}
      </TableCell>
      <TableCell verticalAlign="top" minWidth="200px">
        {hasBulkViolation(c) ? (
          <VStack align="flex-start" gap={1}>
            <Badge status="negative">
              {t('page:biddingCommon.bulkApprove.limit.violation', {
                count: violationCount,
              })}
            </Badge>
            {c.violationSections
              .flatMap((s) => s.details)
              .map((detail) => (
                <Text key={detail} color="Text.midEmphasis" fontSize="xs">
                  {detail}
                </Text>
              ))}
            <Text color="Text.midEmphasis" fontSize="xs">
              {t('page:biddingCommon.bulkApprove.limit.notApproved')}
            </Text>
          </VStack>
        ) : hasBulkUnverified(c) ? (
          // 判定できていない案件は「違反・承認されません」 とは出さない (送れないので結果も無い)。
          <VStack align="flex-start" gap={1}>
            {c.unverifiedSections
              .flatMap((s) => s.details)
              .map((detail) => (
                <Text key={detail} color="Text.midEmphasis" fontSize="xs">
                  {detail}
                </Text>
              ))}
          </VStack>
        ) : (
          <Text color="Text.lowEmphasis">
            {t('page:biddingCommon.bulkApprove.limit.ok')}
          </Text>
        )}
      </TableCell>
      <TableCell verticalAlign="top" minWidth="104px">
        {inversionDetails.length > 0 ? (
          <VStack align="flex-start" gap={1}>
            <Badge status="warning">
              {t('page:biddingCommon.bulkApprove.inversion.found')}
            </Badge>
            {inversionDetails.map((detail) => (
              <Text key={detail} color="Text.midEmphasis" fontSize="xs">
                {detail}
              </Text>
            ))}
          </VStack>
        ) : (
          <Text color="Text.lowEmphasis">
            {t('page:biddingCommon.bulkApprove.inversion.none')}
          </Text>
        )}
      </TableCell>
    </TableRow>
  )
})

/**
 * まとめて承認の確認モーダル (AC-BSP-118。 design `spot-bulk-approve.js` の `renderModal`)。
 *
 * 案件の選択・選択に追随する合計・案件ごとの入札制限と売買逆転の事前チェック・承認者非該当 /
 * 自己承認の再確認を 1 画面で済ませる (クリック数を増やさない)。 入札制限に違反する案件も
 * 選択に含めたまま送れて、 承認可否は backend が決める。
 */
const BulkApproveDialog = ({ flow, targetDate }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const sel = useBulkSelection(flow.cases)
  const { summary } = sel
  const caseIds = useMemo(() => flow.cases.map((c) => c.id), [flow.cases])
  const virtual = useVirtualRows(caseIds)
  const violatedCount = sel.selected.filter(hasBulkViolation).length

  const footer = (
    <SaveCancelFooter
      isPending={flow.isRunning}
      onCancel={flow.close}
      onSave={() => {
        void flow.confirm(sel)
      }}
      saveDisabledReason={sel.confirmBlockedReason}
      saveLabel={t('page:biddingCommon.bulkApprove.action')}
      saveLoadingText={t('page:biddingCommon.bulkApprove.submitting')}
    />
  )

  return (
    <Modal
      footer={footer}
      isOpen
      onClose={flow.close}
      scrollBehavior="inside"
      size="xl"
      title={t('page:biddingCommon.bulkApprove.modalTitle')}
    >
      <VStack align="stretch" gap={3}>
        <Text>{t('page:biddingCommon.bulkApprove.body')}</Text>
        <Text color="Text.midEmphasis" fontSize="sm">
          {t('page:biddingCommon.bulkApprove.deliveryDate', {
            date: formatDateDisplay(targetDate),
          })}
        </Text>
        <Box
          padding={3}
          fontSize="sm"
          borderRadius="md"
          backgroundColor="Surface.secondary"
        >
          <Flex flexWrap="wrap" gap={2} columnGap={6}>
            <Text>
              {t('page:biddingCommon.bulkApprove.summary.count')}{' '}
              <Text as="span" fontWeight="semibold">
                {t('page:biddingCommon.bulkApprove.summary.countValue', {
                  count: summary.count,
                })}
              </Text>
            </Text>
            <Text>
              {t('page:biddingCommon.bulkApprove.summary.buy')}{' '}
              <Text as="span" fontWeight="semibold">
                {t('page:biddingCommon.bulkApprove.summary.quantityValue', {
                  quantity: mw(summary.buyMw),
                })}
              </Text>
            </Text>
            <Text>
              {t('page:biddingCommon.bulkApprove.summary.sell')}{' '}
              <Text as="span" fontWeight="semibold">
                {t('page:biddingCommon.bulkApprove.summary.quantityValue', {
                  quantity: mw(summary.sellMw),
                })}
              </Text>
            </Text>
          </Flex>
          <Flex flexWrap="wrap" gap={1} columnGap={4} marginTop={2}>
            <Text color="Text.midEmphasis">
              {t('page:biddingCommon.bulkApprove.summary.areaBreakdown')}
            </Text>
            {summary.areaRows.length === 0 ? (
              <Text color="Text.lowEmphasis">—</Text>
            ) : (
              summary.areaRows.map((row) => (
                <Text key={row.areaCode} color="Text.midEmphasis">
                  {t('page:biddingCommon.bulkApprove.summary.areaRow', {
                    area: row.areaLabel,
                    buy: mw(row.buyMw),
                    sell: mw(row.sellMw),
                  })}
                </Text>
              ))
            )}
          </Flex>
        </Box>
        {/* 確定を止めている理由は本文の上部にも出す (action-reason.md 区分 1。 ボタンには
            同じ理由のツールチップ)。 */}
        {sel.unverifiedSections.map((section) => (
          <Alert
            key={section.title}
            description={section.body}
            status="info"
            title={section.title}
          />
        ))}
        {violatedCount > 0 && (
          <Alert
            description={t('page:biddingCommon.bulkApprove.violationNote.body')}
            status="error"
            title={t('page:biddingCommon.bulkApprove.violationNote.title', {
              count: violatedCount,
            })}
          />
        )}
        {sel.needsReconfirm && (
          <Alert
            description={
              <VStack align="stretch" gap={1}>
                {sel.notApproverCount > 0 && (
                  <Text>
                    {t(
                      'page:biddingSpot.approverWarning.notApproverConfirmApprove',
                    )}
                  </Text>
                )}
                {sel.selfApprovalCount > 0 && (
                  <Text>
                    {t('page:biddingSpot.approverWarning.selfApproval')}
                  </Text>
                )}
                <Checkbox
                  checked={sel.reconfirmed}
                  onCheckedChange={(details: {
                    checked: boolean | 'indeterminate'
                  }) => {
                    sel.setReconfirmed(details.checked === true)
                  }}
                >
                  {t('page:biddingCommon.bulkApprove.reconfirm.check')}
                </Checkbox>
              </VStack>
            }
            status="warning"
            title={t('page:biddingCommon.bulkApprove.reconfirm.title', {
              items: [
                sel.notApproverCount > 0
                  ? t('page:biddingCommon.bulkApprove.reconfirm.notApprover', {
                      count: sel.notApproverCount,
                    })
                  : undefined,
                sel.selfApprovalCount > 0
                  ? t('page:biddingCommon.bulkApprove.reconfirm.selfApproval', {
                      count: sel.selfApprovalCount,
                    })
                  : undefined,
              ]
                .filter((s): s is string => s !== undefined)
                .join(t('page:biddingCommon.bulkApprove.reconfirm.separator')),
            })}
          />
        )}
        <Flex
          alignItems="center"
          gap={3}
          color="Text.midEmphasis"
          fontSize="sm"
        >
          <Checkbox
            checked={sel.allSelected}
            onCheckedChange={(details: {
              checked: boolean | 'indeterminate'
            }) => {
              sel.toggleAll(details.checked === true)
            }}
          >
            {t('page:biddingCommon.bulkApprove.selectAll')}
          </Checkbox>
          <Text>
            {t('page:biddingCommon.bulkApprove.selectionCount', {
              total: flow.cases.length,
              selected: sel.selected.length,
            })}
          </Text>
        </Flex>
        {/* 案件が数千件になりうるので、 見えている行だけを描く。 スクロール領域は design
            (`spot-bulk-approve.js` の `.sba-table-wrap`) と同じ max-height 360px。 */}
        <Box
          ref={virtual.scrollRef}
          overflow="auto"
          maxHeight="360px"
          onScroll={virtual.onScroll}
        >
          <TableRoot>
            <TableHeader css={{ '& th': TABLE_HEADER_CELL_STYLE }}>
              <TableRow>
                <TableColumnHeader
                  width="36px"
                  aria-label={t('page:biddingCommon.bulkApprove.table.select')}
                />
                <TableColumnHeader>
                  {t('page:biddingCommon.bulkApprove.table.case')}
                </TableColumnHeader>
                <TableColumnHeader>
                  {t('page:biddingCommon.bulkApprove.table.area')}
                </TableColumnHeader>
                <TableColumnHeader textAlign="right">
                  {t('page:biddingCommon.bulkApprove.table.buy')}
                </TableColumnHeader>
                <TableColumnHeader textAlign="right">
                  {t('page:biddingCommon.bulkApprove.table.sell')}
                </TableColumnHeader>
                <TableColumnHeader>
                  {t('page:biddingCommon.bulkApprove.table.limit')}
                </TableColumnHeader>
                <TableColumnHeader>
                  {t('page:biddingCommon.bulkApprove.table.inversion')}
                </TableColumnHeader>
              </TableRow>
            </TableHeader>
            <TableBody>
              <SpacerRow height={virtual.topPad} />
              {flow.cases.slice(virtual.start, virtual.end).map((c) => (
                <CaseRow
                  key={c.id}
                  c={c}
                  checked={!sel.deselectedIds.has(c.id)}
                  onMeasure={virtual.measure}
                  onToggle={sel.toggle}
                />
              ))}
              <SpacerRow height={virtual.bottomPad} />
            </TableBody>
          </TableRoot>
        </Box>
      </VStack>
    </Modal>
  )
}

/**
 * まとめて承認の確認モーダル。 選択の状態は中の `BulkApproveDialog` が持ち、 閉じると破棄する
 * (= 切替のたびにページ全体を描き直さず、 開き直しで既定の全件選択に戻る)。
 */
export const BulkApproveModal = ({
  flow,
  targetDate,
}: Props): React.ReactElement | null =>
  flow.isOpen ? <BulkApproveDialog flow={flow} targetDate={targetDate} /> : null
